<template>
  <div class="dropdown-wrapper">
    <div class="dropdown-wrapper__clickable-element" @click="toggleDropdown" ref="domRefClickableElem">
      <slot name="clickableElement" :isDropdownVisible="isDropdownVisible" />
    </div>

    <teleport to="body">
      <div
        :class="{
          'dropdown-wrapper__dropdown-itself': true,
          'dropdown-wrapper__dropdown-itself--invisible': !isDropdownVisible || screenSize === 'mobile',
        }"
        :aria-hidden="!isDropdownVisible || screenSize === 'mobile'"
        :inert="!isDropdownVisible || screenSize === 'mobile'"
        @mouseenter="handleDropdownMouseEnter"
        @mouseleave="handleDropdownMouseLeave"
        ref="domRefDropdown"
        :style="{
          width: props.width === 'auto' ? 'auto' : `${props.width}px`,
          ...(props.zIndex != null ? { zIndex: props.zIndex } : {}),
          ...dropdownPositionStyles,
        }"
        v-click-outside="
          () => {
            if (screenSize === 'mobile') return;
            isDropdownVisible = false;
          }
        "
      >
        <slot name="dropdown" :isDropdownVisible="isDropdownVisible" :close="closeDropdown" :computeDropdownPosition="computeDropdownPosition" />
      </div>
    </teleport>

    <SrpModal
      v-if="isDropdownVisible && screenSize === 'mobile'"
      v-model:isVisible="isDropdownVisible"
      :zIndex="10000"
      :width="props.width === 'auto' ? 0 : props.width"
      maxHeight="410px"
      :isContentPaddings="false"
      :isCloseButtonVisible="props.isMobileModalCloseButtonVisible"
    >
      <template #content>
        <slot name="dropdown" :isDropdownVisible="isDropdownVisible" :close="closeDropdown" :computeDropdownPosition="computeDropdownPosition" />
      </template>
    </SrpModal>
  </div>
</template>

<script setup lang="ts">
import { computePosition, type ComputePositionConfig, type Placement, shift, flip, offset as floatingUiOffset } from "@floating-ui/dom";
import { ref, onMounted, inject, Ref, onBeforeUnmount, watch, nextTick } from "vue";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import { exclusiveGroups, type ExclusiveDropdown } from "./srpDropdownExclusiveGroups";

// Types
import { ScreenSize } from "@contracts/screenSize";

// Global variables
const screenSize = inject("screenSize") as Ref<ScreenSize>;

const props = withDefaults(
  defineProps<{
    offset?: number;
    width?: number | "auto";
    placement?: Placement;
    isMobileModalCloseButtonVisible?: boolean;
    exclusiveGroup?: string | null;
    /** Overrides the default layer. Needed inside a modal opened with a raised zIndex, or the dropdown opens behind it. */
    zIndex?: number | null;
  }>(),
  {
    offset: 0,
    width: "auto",
    placement: "top-start",
    isMobileModalCloseButtonVisible: true,
    exclusiveGroup: null,
    zIndex: null,
  }
);

// Toggle dropdown ============================================================
const isDropdownVisible = ref<boolean>(false);

let showTimeout = null;
let hideTimeout = null;

function closeImmediate() {
  clearTimeout(showTimeout);
  clearTimeout(hideTimeout);
  isDropdownVisible.value = false;
}

const exclusiveInstance: ExclusiveDropdown = { closeImmediate };

function registerExclusive() {
  const group = props.exclusiveGroup;
  if (!group) return;
  let members = exclusiveGroups.get(group);
  if (!members) {
    members = new Set();
    exclusiveGroups.set(group, members);
  }
  members.add(exclusiveInstance);
}

function unregisterExclusive() {
  const group = props.exclusiveGroup;
  if (!group) return;
  const members = exclusiveGroups.get(group);
  if (!members) return;
  members.delete(exclusiveInstance);
  if (members.size === 0) exclusiveGroups.delete(group);
}

function closeOthersInExclusiveGroup() {
  const group = props.exclusiveGroup;
  if (!group) return;
  for (const member of exclusiveGroups.get(group) ?? []) {
    if (member !== exclusiveInstance) member.closeImmediate();
  }
}

function toggleDropdown() {
  if (isDropdownVisible.value) {
    return;
  }

  closeOthersInExclusiveGroup();

  if (screenSize.value !== "mobile") {
    computeDropdownPositionBeforeOpen();
  }

  clearTimeout(showTimeout);
  clearTimeout(hideTimeout);
  showTimeout = setTimeout(() => (isDropdownVisible.value = true), 100);
}
function closeDropdown(afterClose?: () => void) {
  clearTimeout(showTimeout);
  clearTimeout(hideTimeout);
  hideTimeout = setTimeout(async () => {
    isDropdownVisible.value = false;
    if (afterClose) {
      await nextTick();
      afterClose();
    }
  }, 100);
}

function handleDropdownMouseEnter() {
  clearTimeout(showTimeout);
  clearTimeout(hideTimeout);
}
function handleDropdownMouseLeave() {
  // hideTimeout = setTimeout(() => (isDropdownVisible.value = false), 100);
}

// Set dropdown position ======================================================
const domRefClickableElem = ref(null);
const domRefDropdown = ref(null);
const dropdownPositionStyles = ref({});

const dropdownPlacement = ref<Placement>("top");

onMounted(() => {
  registerExclusive();
  if (screenSize.value !== "mobile") {
    computeDropdownPositionBeforeOpen();
  }
});
onBeforeUnmount(() => unregisterExclusive());

function computeDropdownPosition(): void {
  if (!isDropdownVisible.value) {
    return;
  }

  computeDropdownPositionBeforeOpen();
}

function computeDropdownPositionBeforeOpen(): void {
  if (screenSize.value === "mobile" || !domRefClickableElem.value || !domRefDropdown.value) {
    return;
  }

  const floatingUiOptions: ComputePositionConfig = {
    middleware: [floatingUiOffset(props.offset), flip(), shift()],
    placement: props.placement,
    strategy: "fixed",
  };

  computePosition(domRefClickableElem.value, domRefDropdown.value, floatingUiOptions).then(position => {
    dropdownPlacement.value = position.placement;

    dropdownPositionStyles.value = {
      top: `${position.y}px`,
      left: `${position.x}px`,
    };
  });
}

// Recalculate position on global scroll ======================================
onMounted(() => window.addEventListener("scroll", computeDropdownPosition, { capture: true }));
onBeforeUnmount(() => window.removeEventListener("scroll", computeDropdownPosition, { capture: true }));

// Recalculate position on window resize ======================================
onMounted(() => window.addEventListener("resize", computeDropdownPosition));
onBeforeUnmount(() => window.removeEventListener("resize", computeDropdownPosition));

// Recalculate position on SrpModalScroll =====================================
// (if SrpDropdown is inside the modal)
const srpModalScrollTop = inject("srpModalScrollTop", null) as Ref<number> | null;
if (srpModalScrollTop) {
  watch(srpModalScrollTop, computeDropdownPosition);
}
</script>

<style scoped lang="scss">
@import "@/scss/z-indexes.scss";

// Dropdown wrapper ===========================================================
.dropdown-wrapper {
  display: inline-flex;
  position: relative;

  &__dropdown-itself {
    border-radius: 5px;
    position: fixed;
    z-index: $z-index-tooltip;
    color: #5b5b5b;
    font: 14px/18px sans-serif;
    background: #fff;
    opacity: 1;
    box-shadow: 0 5px 35px -9px rgba(0, 0, 0, 0.4);
    transition: opacity 0.05s ease-in-out;

    &--invisible {
      opacity: 0;
      pointer-events: none;
    }
  }
}
</style>
